/**
 * AuthContext — Google SSO via @react-oauth/google.
 *
 * Flow (mirrors parkify-pay/ops):
 *   1. User clicks "Continue with Google" → useGoogleLogin opens the popup
 *   2. Google returns an access_token in onSuccess
 *   3. We POST { accessToken, rememberMe } to /auth/google on parkify-sales
 *   4. Backend verifies via Google userinfo, gates on @parkify.com,
 *      issues a session JWT
 *   5. JWT + profile stored in localStorage (8h or 7d TTL)
 *   6. authHeaders() returns Authorization: Bearer <jwt> for outgoing fetches
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import { useGoogleLogin } from '@react-oauth/google'

export interface AuthUser {
  sub: string
  email: string
  name?: string
  picture?: string
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  error: string | null
  signInGoogle: (rememberMe?: boolean) => void
  signOut: () => void
  authHeaders: () => HeadersInit
  apiBase: string
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_KEY = 'parkify_sales_portal_auth_v1'

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ??
  'https://parkify-sales-webhook-wqvcihzt7q-uc.a.run.app'

interface StoredAuth {
  user: AuthUser
  token: string
  expiresAt: number
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAuth
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) return null
    return parsed
  } catch {
    return null
  }
}

function saveStored(a: StoredAuth) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(a))
}

function clearStored() {
  localStorage.removeItem(STORAGE_KEY)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredAuth | null>(() => loadStored())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const exchangeToken = useCallback(async (accessToken: string, rememberMe: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, rememberMe }),
      })
      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        let msg = `Sign-in failed (${res.status})`
        try {
          const parsed = JSON.parse(detail) as { detail?: string; error?: string }
          msg = parsed.detail ?? parsed.error ?? msg
        } catch { /* keep msg */ }
        setError(msg)
        return
      }
      const body = (await res.json()) as {
        token: string
        sub: string
        email: string
        name?: string
        picture?: string
        expires_in: number
      }
      const user: AuthUser = {
        sub: body.sub,
        email: body.email,
        name: body.name,
        picture: body.picture,
      }
      const fresh: StoredAuth = {
        user,
        token: body.token,
        expiresAt: Date.now() + body.expires_in * 1000,
      }
      saveStored(fresh)
      setStored(fresh)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed')
    } finally {
      setLoading(false)
    }
  }, [])

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      void exchangeToken(tokenResponse.access_token, pendingRememberMe.current)
    },
    onError: () => setError('Google sign-in canceled or failed.'),
    scope: 'openid email profile',
  })

  // useGoogleLogin doesn't pass arbitrary args through; smuggle rememberMe
  // via a ref captured at click time.
  const pendingRememberMe = useMemo(() => ({ current: false }), [])

  const signInGoogle = useCallback(
    (rememberMe = false) => {
      pendingRememberMe.current = rememberMe
      googleLogin()
    },
    [googleLogin, pendingRememberMe],
  )

  const signOut = useCallback(() => {
    clearStored()
    setStored(null)
  }, [])

  const authHeaders = useCallback((): HeadersInit => {
    if (!stored) return {}
    return { Authorization: `Bearer ${stored.token}` }
  }, [stored])

  useEffect(() => {
    if (!stored) return
    if (stored.expiresAt < Date.now()) signOut()
  }, [stored, signOut])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: stored?.user ?? null,
      loading,
      error,
      signInGoogle,
      signOut,
      authHeaders,
      apiBase: API_BASE,
    }),
    [stored, loading, error, signInGoogle, signOut, authHeaders],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
